import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ListingInterestReview from './ListingInterestReview';
import { AuthContext } from '../../contexts/AuthContext';
import { hasViewedListingBack, listingReviewViewer, rememberListingBack } from '../../utils/listingReviewHistory';
import useInterestFitConfirmation from '../InterestFit/useInterestFitConfirmation';
import { isSignedInProvider, listingIdOf, listingSubjectOf } from '../InterestFit/interestFitSubject';

/**
 * Await this before recording interest, including anonymous and reversal paths.
 *
 * Two doors behind one call:
 *
 *   A SIGNED-IN PROVIDER gets the interest fit confirmation: the listing
 *   compared with their own profile, aspect by aspect, and one explicit
 *   confirm. Every time, on every right swipe or heart. Flipping the card
 *   earlier does not skip it, because the point is to confirm with the
 *   discrepancies in view, not to prove the back of the card was seen.
 *
 *   EVERYONE ELSE (anonymous visitors, who have no profile to compare
 *   against) keeps the card walk-through: front, back, full posting, and an
 *   acknowledgement, skipped once the back has been viewed this session.
 */
export default function useListingReview() {
  const authContext = useContext(AuthContext);
  const auth = authContext?.auth;
  const viewer = listingReviewViewer(auth);
  const signedInProvider = isSignedInProvider(auth);
  const { requestConfirmation, cancelPending, confirmationDialog } = useInterestFitConfirmation();
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);

  const requestReview = useCallback((listing) => {
    // Ignore repeated clicks instead of replacing a pending resolver or
    // allowing two callers to record the same interest after one review.
    if (pendingRef.current || !listing) return Promise.resolve(false);
    if (signedInProvider) {
      const listingId = listingIdOf(listing);
      if (!listingId) return Promise.resolve(false);
      return requestConfirmation({
        audience: 'provider',
        listingId,
        subject: listingSubjectOf(listing),
      });
    }
    if (hasViewedListingBack(listing, viewer)) return Promise.resolve(true);
    return new Promise((resolve) => {
      const request = { listing, viewer, resolve };
      pendingRef.current = request;
      setPending(request);
    });
  }, [viewer, signedInProvider, requestConfirmation]);

  const finish = useCallback((reviewed) => {
    const request = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (reviewed && request) rememberListingBack(request.listing, request.viewer);
    request?.resolve(reviewed);
  }, []);

  useEffect(() => {
    if (pendingRef.current && pendingRef.current.viewer !== viewer) finish(false);
  }, [viewer, finish]);

  // A viewer change (sign-out, account switch) while the fit dialog is open
  // would otherwise confirm interest as the wrong person.
  const viewerRef = useRef(viewer);
  useEffect(() => {
    if (viewerRef.current !== viewer) cancelPending();
    viewerRef.current = viewer;
  }, [viewer, cancelPending]);

  useEffect(() => () => {
    pendingRef.current?.resolve(false);
    pendingRef.current = null;
  }, []);

  return {
    requestReview,
    reviewDialog: confirmationDialog || (pending ? (
      <ListingInterestReview
        listing={pending.listing}
        onCancel={() => finish(false)}
        onConfirm={() => finish(true)}
      />
    ) : null),
  };
}
